import { useState, useEffect } from "react";
import { useHistory, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import Checkbox from "@mui/material/Checkbox";
import Divider from "@mui/material/Divider";
import FormControlLabel from "@mui/material/FormControlLabel";
import MuiAutocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";

import { BasePaper, Row } from "../../../components/Base";
import Button from "../../../components/Button";
import {
  AutocompleteForm,
  DateTimePickerCustom,
  Form,
  FormFieldValue,
  InputForm,
  Switch,
  Validation,
} from "../../../components/Form";

import { Person } from "src/api/base";
import { CourseShort, getMoodleCoursesForTraining } from "src/api/courses";
import { createTraining, getTraining, updateTraining, Training } from "src/api/training";
import { getCrews, CrewShort } from "src/api/crew";
import { getPositions, mapPositionsByName, Position } from "src/api/positions";
import { filterPersonsByRole, getAllEvaluators, PersonRole } from "src/api/user";
import courseStatusService, { CourseStatus, CourseStatusType } from "src/services/courseStatus";
import classroomService, { Classroom, ClassroomType } from "src/services/classroom";
import trainingContextService, { TrainingContext, TrainingContextType } from "src/services/trainingContext";
import practicalTrainingService, { PracticalTraining, PracticalTrainingType } from "src/services/practicalTraining";
import validityCalculationService, {
  ValidityCalculation,
  ValidityCalculationType,
} from "src/services/validityCalculation";

interface TrainingForm {
  context: TrainingContext;
  displayname?: string;
  positions?: Position[];
  classification?: string;
  status: CourseStatus;
  trainingSlot?: number;
  trainingStartDate?: string;
  trainingEndDate?: string;
  validityCalculation: ValidityCalculation;
  crews?: CrewShort[];

  isELearning?: boolean;
  eLearningMoodle?: CourseShort | null;

  isClassroom?: boolean;
  classroom?: Classroom | null;
  location?: string;
  virtualClassroomLink?: string;
  classroomResourceLink?: string | null;
  classroomAttendance?: boolean;
  classroomCheck?: boolean;
  writtenTest?: boolean;
  classroomMoodle?: CourseShort | null;
  trainers?: Person[];

  isPractical?: boolean;
  practicalTraining?: PracticalTraining[];
  instructorComplete?: boolean;
  instructors?: Person[];
  assessorPassed?: boolean;
  assessors?: Person[];
  checkerPassed?: boolean;
  checkers?: Person[];

  // Just resending back as it comes from the API
  courseId?: number;
  eLearningCheck?: number;

  // Backward compatibility
  courses: [];
}

const mapTrainingForm = (
  training: Training | null,
  positionOptions: Position[],
  courseOptions: CourseShort[],
): TrainingForm => ({
  context: trainingContextService.getByCode(training?.context || TrainingContextType.INDIVIDUAL)!,
  displayname: training?.displayname,
  classification: training?.classification,
  positions: mapPositionsByName(training?.positions?.split("; "), positionOptions),
  status: courseStatusService.getByCode(training?.status || CourseStatusType.ACTIVE)!,
  trainingSlot: training?.trainingSlot,
  trainingStartDate: training?.trainingStartDate,
  trainingEndDate: training?.trainingEndDate,
  validityCalculation: validityCalculationService.getByCode(
    training?.validityCalculation || ValidityCalculationType.ALL_PARTS,
  )!,
  crews: training?.crews,

  isELearning: training?.isELearning === 1,
  eLearningMoodle: courseOptions.find((c) => c.id === training?.eLearningMoodle) || null,

  isClassroom: training?.isClassroom === 1,
  classroom: classroomService.getByCode(training?.classroom || ClassroomType.ON_SITE)!,
  location: training?.location || undefined,
  virtualClassroomLink: training?.virtualClassroomLink,
  classroomResourceLink: training?.classroomResourceLink,
  classroomAttendance: training?.classroomAttendance === 1,
  classroomCheck: training?.classroomCheck === 1,
  writtenTest: training?.writtenTest === 1,
  classroomMoodle: courseOptions.find((c) => c.id === training?.classroomMoodle) || null,
  trainers: training?.trainers,

  isPractical: training?.isPractical === 1,
  practicalTraining: practicalTrainingService.parsePracticalTrainingString(training?.practicalTraining),
  instructorComplete: training?.instructorComplete === 1,
  instructors: training?.instructors,
  assessorPassed: training?.assessorPassed === 1,
  assessors: training?.assessors,
  checkerPassed: training?.checkerPassed === 1,
  checkers: training?.checkers,

  // Just resending back as it comes from the API
  courseId: training?.courseId,
  eLearningCheck: training?.eLearningCheck,

  courses: [],
});

const TrainingEditForm = () => {
  const history = useHistory();
  const goBack = () => history.goBack();
  const { t } = useTranslation();

  // spellchecker:disable-next-line
  const classificationItems = ["in den Gefahrenklassen 1 – 6, 8 + 9 inkl. RRE", "in den Gefahrenklassen 1 – 9"];

  const { trainingId: trainingIdParam } = useParams<{ trainingId: string }>();
  const trainingId = trainingIdParam ? parseInt(trainingIdParam, 10) : undefined;
  const isAdd = !trainingIdParam;

  const [loaded, setLoaded] = useState(false);
  const [training, setTraining] = useState<TrainingForm | null>(null);
  const [positionOptions, setPositionOptions] = useState<Position[]>([]);
  const [courseStatusOptions, setCourseStatusOptions] = useState<CourseStatus[]>([]);
  const [crewOptions, setCrewOptions] = useState<CrewShort[]>([]);
  const [courseOptions, setCourseOptions] = useState<CourseShort[]>([]);
  const [classroomOptions, setClassroomOptions] = useState<Classroom[]>([]);
  const [trainingContextOptions, setTrainingContextOptions] = useState<TrainingContext[]>([]);
  const [practicalTrainingOptions, setPracticalTrainingOptions] = useState<PracticalTraining[]>([]);
  const [validityCalculationOptions, setValidityCalculationOptions] = useState<ValidityCalculation[]>([]);
  const [trainerOptions, setTrainerOptions] = useState<Person[]>([]);
  const [instructorOptions, setInstructorOptions] = useState<Person[]>([]);
  const [assessorOptions, setAssessorOptions] = useState<Person[]>([]);
  const [checkerOptions, setCheckerOptions] = useState<Person[]>([]);

  // Fields handled out of the form
  // it looks buggy, is this even needed?
  const [selectedClassificationItems, setSelectedClassificationItems] = useState<string[]>([]);
  const [inputValue, setInputValue] = useState("");

  // Redirect if ID is not a valid number
  useEffect(() => {
    if (trainingIdParam && isNaN(Number(trainingIdParam))) {
      goBack();
    }
  }, [trainingIdParam]);

  useEffect(() => {
    (async () => {
      const [positionOptions, crewOptions, courseOptions, evaluatorOptions, training] = await Promise.all([
        getPositions(),
        getCrews(),
        getMoodleCoursesForTraining(),
        getAllEvaluators(),
        // Conditionally fetch training data in case of edit form
        trainingId ? getTraining(trainingId) : Promise.resolve(null),
      ]);

      const trainerOptions = filterPersonsByRole(evaluatorOptions, PersonRole.TRAINER);
      const instructorOptions = filterPersonsByRole(evaluatorOptions, PersonRole.INSTRUCTOR);
      const assessorOptions = filterPersonsByRole(evaluatorOptions, PersonRole.ASSESSOR);
      const checkerOptions = filterPersonsByRole(evaluatorOptions, PersonRole.CHECKER);
      setPositionOptions(positionOptions);
      setCrewOptions(crewOptions);
      setCourseOptions(courseOptions);
      setTrainerOptions(trainerOptions);
      setInstructorOptions(instructorOptions);
      setAssessorOptions(assessorOptions);
      setCheckerOptions(checkerOptions);

      // static no API services
      const courseStatusOptions = await courseStatusService.getOptions();
      const classroomOptions = await classroomService.getOptions();
      const trainingContextOptions = await trainingContextService.getOptions();
      const practicalTrainingOptions = await practicalTrainingService.getOptions();
      const validityCalculationOptions = await validityCalculationService.getOptions();

      setCourseStatusOptions(courseStatusOptions);
      setClassroomOptions(classroomOptions);
      setTrainingContextOptions(trainingContextOptions);
      setPracticalTrainingOptions(practicalTrainingOptions);
      setValidityCalculationOptions(validityCalculationOptions);

      if (training) {
        setSelectedClassificationItems(training.classification?.split("; ") || []);
      }
      setTraining(mapTrainingForm(training, positionOptions, courseOptions));

      setLoaded(true);
    })();
  }, [trainingId]);

  const onSubmit = async (trainingForm: TrainingForm) => {
    const trainingRequest = {
      ...trainingForm,
      displayname: trainingForm.displayname!,
      context: trainingForm.context?.code,
      positions: trainingForm.positions?.map((p: Position) => p.getDisplayName()).join("; ") ?? "",
      status: trainingForm.status?.code,
      trainingSlot: +(trainingForm.trainingSlot || 0),
      trainingStartDate: trainingForm.trainingStartDate!,
      trainingEndDate: trainingForm.trainingEndDate!,
      validityCalculation: trainingForm.validityCalculation?.code ?? "",
      crews: trainingForm.crews?.map((c: CrewShort) => c.id) ?? [],

      isELearning: trainingForm.isELearning ? 1 : 0,
      eLearningMoodle: trainingForm.eLearningMoodle?.id ?? undefined,
      virtualClassroomLink: trainingForm.virtualClassroomLink ?? "",

      isClassroom: trainingForm.isClassroom ? 1 : 0,
      classroom: trainingForm.classroom?.code ?? "",
      classroomAttendance: trainingForm.classroomAttendance ? 1 : 0,
      classroomCheck: trainingForm.classroomCheck ? 1 : 0,
      writtenTest: trainingForm.writtenTest ? 1 : 0,
      classroomMoodle: trainingForm.classroomMoodle?.id ?? undefined,
      trainers: trainingForm.trainers?.map((p: Person) => p.id) ?? [],

      isPractical: trainingForm.isPractical ? 1 : 0,
      practicalTraining: practicalTrainingService.formatPracticalTrainingForApi(trainingForm.practicalTraining),
      instructorComplete: trainingForm.instructorComplete ? 1 : 0,
      instructors: trainingForm.instructors?.map((p: Person) => p.id) ?? [],
      assessorPassed: trainingForm.assessorPassed ? 1 : 0,
      assessors: trainingForm.assessors?.map((p: Person) => p.id) ?? [],
      checkerPassed: trainingForm.checkerPassed ? 1 : 0,
      checkers: trainingForm.checkers?.map((p: Person) => p.id) ?? [],

      eLearningCheck: trainingForm.eLearningCheck ?? 0,
      classification: selectedClassificationItems.join("; ") ?? "",
    };

    const result = isAdd ? await createTraining(trainingRequest) : await updateTraining(trainingId!, trainingRequest);

    if (result.ok) {
      history.push("/trainings");
    }
  };

  return (
    <>
      <Typography variant="h4">
        <Button icon={<ArrowBackIcon />} onClick={goBack} />
        {t(`training.title_${isAdd ? "add" : "edit"}`)}
      </Typography>
      {loaded && (
        <BasePaper>
          <Form
            defaultValues={{ ...training }}
            validation={{
              displayname: Validation.required,
              trainingSlot: Validation.required,
              trainingStartDate: Validation.verifyDatetimeFormat,
              trainingEndDate: Validation.verifyDatetimeFormat,
            }}
            onSubmit={onSubmit}
          >
            <Row>
              <AutocompleteForm
                name="context"
                label={t("training.context")}
                items={trainingContextOptions}
                disableClearable
              />
            </Row>

            <Row>
              <InputForm name="displayname" label={`${t("training.name")}*`} />
            </Row>

            <Row>
              <MuiAutocomplete
                size="small"
                freeSolo
                multiple
                inputValue={inputValue}
                onInputChange={(event, newInputValue) => {
                  setInputValue(newInputValue);
                }}
                onKeyPress={(event) => {
                  if (event.key === "Enter") {
                    setSelectedClassificationItems([...selectedClassificationItems, inputValue]);
                    setInputValue("");
                  }
                }}
                options={classificationItems}
                value={selectedClassificationItems || ""}
                onChange={(event, newSelectedValues) => {
                  setSelectedClassificationItems(newSelectedValues);
                }}
                renderInput={(params) => (
                  <TextField {...params} label={t("training.classification")} variant="outlined" />
                )}
              />
            </Row>

            <Row>
              <AutocompleteForm name="positions" label={t("positions")} items={positionOptions} multiple />
            </Row>

            <Row>
              <AutocompleteForm
                name="status"
                label={t("training.course_status")}
                items={courseStatusOptions}
                disableClearable
              />
            </Row>

            <Row>
              <InputForm name="trainingSlot" label={t("training.slots")} type="number" />
            </Row>

            <Row>
              <DateTimePickerCustom name="trainingStartDate" label={t("start_date")} />
            </Row>
            <Row>
              <DateTimePickerCustom name="trainingEndDate" label={t("end_date")} />
            </Row>
            <Row>
              <AutocompleteForm
                name="validityCalculation"
                label={t("training.validity_calculation")}
                items={validityCalculationOptions}
                disableClearable
              />
            </Row>

            <Row>
              <FormFieldValue
                name="crews"
                render={(crews, setCrews) => (
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={Array.isArray(crews) && crews.length === crewOptions.length}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setCrews([...crewOptions]);
                          } else {
                            setCrews([]);
                          }
                        }}
                      />
                    }
                    label={t("training.all_crews")}
                  />
                )}
              />
              <AutocompleteForm name="crews" label={t("training.crews_to_add")} items={crewOptions} multiple />
            </Row>

            <BasePaper>
              <Row>
                <Switch name="isELearning" />
                {t("training.e_learning_module")}
              </Row>
              <FormFieldValue
                name="isELearning"
                render={(isELearning) =>
                  isELearning && (
                    <Row>
                      <AutocompleteForm
                        name="eLearningMoodle"
                        label={t("training.e_learning_test")}
                        items={courseOptions}
                      />
                    </Row>
                  )
                }
              />
            </BasePaper>

            <BasePaper>
              <Row>
                <Switch name="isClassroom" />
                {t("training.classroom_module")}
              </Row>

              <FormFieldValue
                name="isClassroom"
                render={(isClassroom) =>
                  isClassroom && (
                    <>
                      <Row>
                        <AutocompleteForm name="classroom" label={t("classroom")} items={classroomOptions} />
                      </Row>

                      <FormFieldValue
                        name="classroom"
                        render={(classroom) =>
                          classroom &&
                          {
                            [ClassroomType.ON_SITE]: (
                              <Row>
                                <InputForm name="location" label={t("location")} />
                              </Row>
                            ),
                            [ClassroomType.VIRTUAL_CLASSROOM]: (
                              <Row>
                                <InputForm name="virtualClassroomLink" label={t("training.virtual_classroom_link")} />
                              </Row>
                            ),
                          }[classroom.code as ClassroomType]
                        }
                      />

                      <Row>
                        <InputForm name="classroomResourceLink" label={t("training.link_course_documents")} />
                      </Row>

                      <Row>
                        <Switch name="classroomAttendance" /> {t("training.classroom_attendance")}
                      </Row>
                      <Row>
                        <Switch name="classroomCheck" /> {t("training.classroom_test")}
                      </Row>
                      <Row>
                        <Switch name="writtenTest" /> {t("training.classroom_written_test")}
                      </Row>
                      <FormFieldValue
                        name="writtenTest"
                        render={(writtenTest) =>
                          !writtenTest && (
                            <Row>
                              <AutocompleteForm
                                name="classroomMoodle"
                                label={t("training.classroom_moodle_test")}
                                items={courseOptions}
                              />
                            </Row>
                          )
                        }
                      />
                      <FormFieldValue
                        name="context"
                        render={(trainingContext) =>
                          trainingContext == TrainingContextType.INDIVIDUAL && (
                            <Row>
                              <AutocompleteForm name="trainers" label={t("trainers")} items={trainerOptions} multiple />
                            </Row>
                          )
                        }
                      />
                    </>
                  )
                }
              />
            </BasePaper>

            <BasePaper>
              <Row>
                <Switch name="isPractical" />
                {t("training.practical_module")}
              </Row>

              <FormFieldValue
                name="isPractical"
                render={(isPractical) =>
                  isPractical && (
                    <>
                      <Row>
                        <AutocompleteForm
                          name="practicalTraining"
                          label={t("training.practical_training")}
                          items={practicalTrainingOptions}
                          multiple
                        />
                      </Row>
                      <Divider />
                      <Row>
                        <Switch name="instructorComplete" /> {t("training.confirm_instruction_completed")}
                      </Row>

                      <FormFieldValue
                        name="context"
                        render={(trainingContext) =>
                          trainingContext == TrainingContextType.INDIVIDUAL && (
                            <>
                              <Row>
                                <AutocompleteForm
                                  name="instructors"
                                  label={t("instructors")}
                                  items={instructorOptions}
                                  multiple
                                />
                              </Row>
                              <Divider />
                            </>
                          )
                        }
                      />

                      <FormFieldValue
                        name="practicalTraining"
                        render={(practicalTraining) =>
                          practicalTraining.some(
                            // @ts-ignore comparison works at runtime due to valueOf() implementation
                            (pl: PracticalTraining) => pl == PracticalTrainingType.FACE_TO_FACE,
                          ) && (
                            <>
                              <Row>
                                <Switch name="assessorPassed" /> {t("training.practical_assessment")}
                              </Row>

                              <FormFieldValue
                                name="context"
                                render={(trainingContext) =>
                                  trainingContext == TrainingContextType.INDIVIDUAL && (
                                    <>
                                      <Row>
                                        <AutocompleteForm
                                          name="assessors"
                                          label={t("assessors")}
                                          items={assessorOptions}
                                          multiple
                                        />
                                      </Row>
                                      <Divider />
                                    </>
                                  )
                                }
                              />
                            </>
                          )
                        }
                      />

                      <FormFieldValue
                        name="practicalTraining"
                        render={(practicalTraining) =>
                          practicalTraining.some(
                            (pl: PracticalTraining) =>
                              // @ts-ignore comparison works at runtime due to valueOf() implementation
                              pl == PracticalTrainingType.SIMULATION || pl == PracticalTrainingType.HELICOPTER,
                          ) && (
                            <>
                              <Row>
                                <Switch name="checkerPassed" /> {t("training.checker_passed_check")}
                              </Row>

                              <FormFieldValue
                                name="context"
                                render={(trainingContext) =>
                                  trainingContext == TrainingContextType.INDIVIDUAL && (
                                    <>
                                      <Row>
                                        <AutocompleteForm
                                          name="checkers"
                                          label={t("checkers")}
                                          items={checkerOptions}
                                          multiple
                                        />
                                      </Row>
                                      <Divider />
                                    </>
                                  )
                                }
                              />
                            </>
                          )
                        }
                      />
                    </>
                  )
                }
              />
            </BasePaper>

            <Row>
              <Button type="submit">{t("save")}</Button>
              <Button onClick={goBack}>{t("cancel")}</Button>
            </Row>
          </Form>
        </BasePaper>
      )}
    </>
  );
};

export default TrainingEditForm;
